import { ReactComponent as NotFoundIllustration } from "../../assets/images/woman-binoculars-404.svg";
import { Button } from "../../components/Button";
import { styled } from "../../styles";

// The top panel (both the real one and the pre-login bar below) is 62px tall,
// so the error block gets the rest of the viewport and centres itself in it.
const TOP_PANEL_HEIGHT = "62px";

/**
 * Pre-login shell: a white 62px bar with a centred logo, matching the
 * `*-login-404-error` Figma frames. The authenticated frames get the real
 * portal chrome from `DashboardLayout` instead.
 */
export const PreLoginContainer = styled("div", {
  width: "100%",
  minHeight: "100vh",

  display: "flex",
  flexDirection: "column",

  backgroundColor: "$gray-20",
});

export const PreLoginHeader = styled("header", {
  width: "100%",
  height: TOP_PANEL_HEIGHT,
  flexShrink: 0,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  backgroundColor: "$white",
  boxShadow: "0px 0px 4px 1px rgba(0, 0, 0, 0.16)",
});

// Height-only sizing so both brand logos keep their own aspect ratio (the
// pre-login logo is HSE on UAT — see styles/preLoginBranding.ts).
export const PreLoginLogo = styled("img", {
  height: "$13",
  width: "auto",
});

/**
 * The error block itself: the copy in a left column with the illustration
 * alongside it.
 *
 * Mobile is the reason this is not two plain columns all the way down — there
 * the illustration sits *between* the message and the button, so it cannot be
 * a sibling of a wrapper holding all three. `TextColumn` collapses to
 * `display: contents` at `@sm`, which hands its children straight to the grid
 * below and lets the named areas reorder them.
 */
export const Content = styled("div", {
  boxSizing: "border-box",
  width: "100%",
  minHeight: `calc(100vh - ${TOP_PANEL_HEIGHT})`,
  padding: "$16 $13",

  display: "flex",
  gap: "$18",
  justifyContent: "center",
  alignItems: "center",

  "@md": {
    gap: "$13",
  },

  "@sm": {
    padding: "$13 $11 $14",

    display: "grid",
    gap: 0,
    alignContent: "start",
    justifyContent: "center",
    gridTemplateColumns: "minmax(0, 280px)",
    gridTemplateAreas: `
      "heading"
      "body"
      "illustration"
      "button"
    `,
  },
});

/**
 * Keeps the heading, the message and the button as one block whose height is
 * its own content. As grid rows next to a row-spanning illustration they were
 * stretched to its height instead, which dealt each of them ~100px of dead
 * space and is what the design review flagged.
 */
export const TextColumn = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",

  "@sm": {
    display: "contents",
  },
});

export const Heading = styled("h1", {
  gridArea: "heading",
  margin: 0,

  color: "$gray-100",
  fontFamily: "$nunito",
  fontWeight: 700,
  fontSize: "96px",
  // Nunito Sans' default line box is 1.364em, which parks ~34px of empty
  // leading under the baseline and pushes the body copy far below where the
  // design has it. Pinning it to the font size lets the margin below control
  // the gap on its own.
  lineHeight: 1,
  letterSpacing: "3px",
  whiteSpace: "nowrap",

  "@sm": {
    fontSize: "64px",
    letterSpacing: "2px",
  },
});

export const Message = styled("p", {
  gridArea: "body",
  // Design review: the gap the design shows is ~39px from the "404 error"
  // baseline to the top of the copy, which this lands on once the heading's
  // leading is out of the way.
  margin: "$8 0 0 0",
  maxWidth: "440px",

  color: "$gray-100",
  fontFamily: "$openSans",
  fontWeight: 400,
  fontSize: "20px",
  lineHeight: "30px",

  "@sm": {
    maxWidth: "280px",

    fontSize: "$text-xl",
    lineHeight: "26px",
  },
});

/**
 * "contact us" is a real button, not an anchor: it opens the Intercom panel
 * in place for a signed-in user, so there is no href to give it.
 */
export const ContactUsButton = styled("button", {
  padding: 0,
  border: "none",
  background: "none",

  color: "inherit",
  font: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "3px",

  cursor: "pointer",

  "&:hover": {
    textDecorationThickness: "2px",
  },

  "&:focus-visible": {
    outline: "2px solid $gray-80",
    outlineOffset: "2px",
    borderRadius: "$1",
  },
});

export const Illustration = styled(NotFoundIllustration, {
  gridArea: "illustration",
  alignSelf: "center",
  opacity: 0.88,

  width: "411px",
  height: "558px",

  "@md": {
    width: "359px",
    height: "487px",
  },

  "@sm": {
    width: "222px",
    height: "301px",

    justifySelf: "center",
    margin: "$13 0 0 0",
  },
});

// The shared Button is already the right height, radius and type; the design
// pins it to a fixed 212px with centred label, which the component's own
// styles don't do (its inner <button> is left-aligned and content-width).
export const TryAgainButton = styled(Button, {
  gridArea: "button",
  width: "212px",
  // ~64px below the copy, as measured off the design.
  marginTop: "$16",

  button: {
    width: "100%",
    justifyContent: "center",
  },

  "@sm": {
    justifySelf: "center",
    marginTop: "$15",
  },
});
